import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  FolderOpenIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { api } from '../services/authService';

const ContractClausesLibraryPage = () => {
  const [clauses, setClauses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingClause, setEditingClause] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [formData, setFormData] = useState({
    category_id: '',
    title: '',
    content: '',
    is_optional: 1
  });

  useEffect(() => {
    loadClauses();
    loadCategories();
  }, []);

  const loadClauses = async () => {
    try {
      setLoading(true);

      const response = await api.get('/api/contracts/clauses/list');
      const data = response.data;
      setClauses(data.clauses || []);
    } catch (error) {
      console.error('Erro ao carregar cláusulas:', error);
      toast.error('Erro ao carregar cláusulas');
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await api.get('/api/contracts/clauses/categories');
      const data = response.data;
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = { ...formData, active: 1 };

      if (editingClause) {
        await api.put(`/api/contracts/clauses/${editingClause.id}`, payload);
      } else {
        await api.post('/api/contracts/clauses', payload);
      }

      toast.success(editingClause ? 'Cláusula atualizada!' : 'Cláusula criada!');
      loadClauses();
      closeModal();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      const message = error.response?.data?.error || 'Erro ao salvar cláusula';
      toast.error(message);
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
    }
  };

  const handleEdit = (clause) => {
    setEditingClause(clause);
    setFormData({
      category_id: clause.category_id,
      title: clause.title,
      content: clause.content,
      is_optional: clause.is_optional
    });
    setShowModal(true);
  };

  const handleDelete = async (id, title) => {
    const confirmMessage = `⚠️ ATENÇÃO: Você está prestes a EXCLUIR PERMANENTEMENTE a cláusula:\n\n"${title}"\n\nEsta ação NÃO PODE SER DESFEITA!\n\nContratos existentes que usam esta cláusula não serão afetados, mas ela não estará mais disponível para novos contratos.\n\nDeseja realmente continuar?`;

    if (!confirm(confirmMessage)) return;

    try {
      await api.delete(`/api/contracts/clauses/${id}`);
      toast.success('Cláusula excluída permanentemente!');
      loadClauses();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      const message = error.response?.data?.error || 'Erro ao excluir cláusula';
      toast.error(message);
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
    }
  };

  const handleDeleteCategory = async (id, name, clausesCount) => {
    if (clausesCount > 0) {
      toast.error(`Não é possível excluir a categoria "${name}" pois ela possui ${clausesCount} cláusula(s). Exclua todas as cláusulas primeiro.`);
      return;
    }

    const confirmMessage = `⚠️ ATENÇÃO: Você está prestes a EXCLUIR PERMANENTEMENTE a categoria:\n\n"${name}"\n\nEsta ação NÃO PODE SER DESFEITA!\n\nDeseja realmente continuar?`;

    if (!confirm(confirmMessage)) return;

    try {
      await api.delete(`/api/contracts/clauses/categories/${id}`);
      toast.success('Categoria excluída permanentemente!');
      loadCategories();
      loadClauses();
    } catch (error) {
      console.error('Erro ao excluir categoria:', error);
      const message = error.response?.data?.error || 'Erro ao excluir categoria';
      toast.error(message);
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingClause(null);
    setFormData({
      category_id: '',
      title: '',
      content: '',
      is_optional: 1
    });
  };

  const filteredClauses = filterCategory === 'all'
    ? clauses
    : clauses.filter((c) => c.category_id === parseInt(filterCategory));

  const clausesByCategory = categories.map((category) => ({
    ...category,
    clauses: filteredClauses.filter((c) => c.category_id === category.id)
  }));

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Biblioteca de Cláusulas
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Gerencie as cláusulas contratuais do sistema
          </p>
        </div>

        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          Nova Cláusula
        </button>
      </div>

      {/* Filtros */}
      <div className="mb-6 flex items-center gap-4">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Filtrar por categoria:
        </label>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="all">Todas as categorias</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.name}
            </option>
          ))}
        </select>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
            {clauses.length}
          </div>
          <div className="text-sm text-blue-700 dark:text-blue-300">
            Total de Cláusulas
          </div>
        </div>

        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
            {clauses.filter((c) => c.is_optional === 0).length}
          </div>
          <div className="text-sm text-green-700 dark:text-green-300">
            Obrigatórias
          </div>
        </div>

        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
          <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
            {clauses.filter((c) => c.is_optional === 1).length}
          </div>
          <div className="text-sm text-yellow-700 dark:text-yellow-300">
            Opcionais
          </div>
        </div>

        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
          <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
            {categories.length}
          </div>
          <div className="text-sm text-purple-700 dark:text-purple-300">
            Categorias
          </div>
        </div>
      </div>

      {/* Lista de Cláusulas por Categoria */}
      {loading ? (
        <div className="text-center py-12">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Carregando...</p>
        </div>
      ) : (
        <div className="space-y-6">
          {clausesByCategory.map((category) => (
            <div
              key={category.id}
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              {/* Cabeçalho da Categoria */}
              <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{category.icon || '📁'}</span>
                      <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                        {category.category_number || category.display_order}.
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {category.name}
                    </h3>
                    <span className="px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded">
                      {category.clauses.length}
                    </span>
                  </div>

                  <button
                    onClick={() => handleDeleteCategory(category.id, category.name, category.clauses.length)}
                    className="p-2 bg-red-100 dark:bg-red-900/20 hover:bg-red-200 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 rounded transition-colors"
                    title="Excluir categoria permanentemente"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
                {category.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {category.description}
                  </p>
                )}
              </div>

              {/* Cláusulas */}
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {category.clauses.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    Nenhuma cláusula nesta categoria
                  </div>
                ) : (
                  category.clauses.map((clause) => (
                    <div
                      key={clause.id}
                      className="p-6 hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            {clause.clause_number && (
                              <span className="text-sm font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded">
                                {clause.clause_number}
                              </span>
                            )}
                            <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                              {clause.title}
                            </h4>
                            {clause.is_optional === 0 ? (
                              <span className="px-2 py-1 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 text-xs font-medium rounded">
                                Obrigatória
                              </span>
                            ) : (
                              <span className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 text-xs font-medium rounded">
                                Opcional
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
                            {clause.content}
                          </p>
                        </div>

                        <div className="flex gap-2 flex-shrink-0">
                          <button
                            onClick={() => handleEdit(clause)}
                            className="p-2 bg-blue-100 dark:bg-blue-900/20 hover:bg-blue-200 dark:hover:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded transition-colors"
                            title="Editar cláusula"
                          >
                            <PencilSquareIcon className="w-5 h-5" />
                          </button>

                          <button
                            onClick={() => handleDelete(clause.id, clause.title)}
                            className="p-2 bg-red-100 dark:bg-red-900/20 hover:bg-red-200 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 rounded transition-colors"
                            title="Excluir cláusula permanentemente"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de Criação/Edição */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                {editingClause ? 'Editar Cláusula' : 'Nova Cláusula'}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Categoria */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Categoria *
                  </label>
                  <select
                    value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  >
                    <option value="">Selecione uma categoria</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Título */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Título *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="CLÁUSULA Xª - DO TÍTULO"
                    required
                  />
                </div>

                {/* Conteúdo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Conteúdo *
                  </label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    rows={10}
                    placeholder="Conteúdo da cláusula... Use {{variavel}} para variáveis dinâmicas."
                    required
                  />
                </div>

                {/* Opcional */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_optional"
                    checked={formData.is_optional === 1}
                    onChange={(e) => setFormData({ ...formData, is_optional: e.target.checked ? 1 : 0 })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="is_optional" className="text-sm text-gray-700 dark:text-gray-300">
                    Esta cláusula é opcional (pode ser adicionada/removida pelos usuários)
                  </label>
                </div>

                {/* Botões */}
                <div className="flex justify-end gap-2 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    {editingClause ? 'Atualizar' : 'Criar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractClausesLibraryPage;